import { ChangeDetectorRef, Component, effect, inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { SharedService } from '../../shared/services/shared.service';
import { User } from '../../model/user.model';
import { AskQuestionModel, langAndCaddy } from '../../model/chat.model';
import { catchError, forkJoin, map, Observable, of, Subscription, switchMap, take, filter, pairwise, startWith } from 'rxjs';
import { UserSettingService } from '../../services/user-setting.service';
import { AuthService } from '../../auth/auth.service';
import { ChatService } from '../../services/chat/chat.service';
import { MediaService } from '../../services/media.service';
import { TranslationService } from '../../shared/translation.service';
import { ToasterService } from '../../shared/services/toaster.service';
import { isPlatformBrowser } from '@angular/common';
import { TokenService } from '../../auth/token.service';
import { BackButton } from '../../model/shared.model';

@Component({
  selector: 'app-header',
  imports: [
    TranslatePipe,
    RouterLink,
    RouterLinkActive
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit, OnDestroy {
  showMenu = false;
  user = new User();
  isLoggedIn = false;
  isSignedIn = false;
  url = '';
  _sharedService = inject(SharedService);
  _cdr = inject(ChangeDetectorRef);
  subscriptions!: Subscription;
  _settingService = inject(UserSettingService);
  _authService = inject(AuthService)
  _chatService = inject(ChatService);
  _mediaService = inject(MediaService);
  _translationService = inject(TranslationService);
  _toaster = inject(ToasterService);
  _platformId = inject(PLATFORM_ID);
  _tokenService = inject(TokenService);
  _router = inject(Router);
  backBtn = new BackButton();
  private navigationHistory: string[] = [];

  constructor(){
    effect(() => {
      this.backBtn = this._sharedService.backButton();
      this._cdr.detectChanges();
    });

    effect(() => {
      this.isSignedIn = this._sharedService.loggedIn();
    });
    
    // Track navigation history and manage back button logic
    this._router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      startWith(null as NavigationEnd | null),
      pairwise()
    ).subscribe(([previousEvent, currentEvent]: [NavigationEnd | null, NavigationEnd | null]) => {
      if (isPlatformBrowser(this._platformId) && document) {
        if(this.backBtn && this.backBtn.action){
          this.setButtonAction(this.backBtn.action);
        }
        
        this.showMenu = false;
        if (currentEvent) {
          this.url = currentEvent.url;
          
          // Add current URL to history if it's not already the last one
          if (this.navigationHistory[this.navigationHistory.length - 1] !== this.url) {
            this.navigationHistory.push(this.url);
            // Keep only last 10 URLs to prevent memory issues
            if (this.navigationHistory.length > 10) {
              this.navigationHistory.shift();
            }
          }
          
          this.setBackButtonLogic(previousEvent?.url || null, this.url);
        }
      }
    })
  }

  private setBackButtonLogic(previousUrl: string | null, currentUrl: string): void {
    // Don't show back button on home page
    if (currentUrl === '/') {
      this._sharedService.backButton.set({ show: false, url: '', text: '', action: '' });
      this._cdr.detectChanges();
      return;
    }

    let backUrl = '/';
    
    // Handle article detail pages
    if (currentUrl.includes('/article/detail/')) {
      if (previousUrl === '/') {
        backUrl = '/';
      } else if (previousUrl?.includes('/article') && !previousUrl.includes('/article/detail/')) {
        backUrl = '/article';
      } else if (previousUrl) {
        backUrl = previousUrl;
      } else {
        // If no previous URL (page refresh), default to article listing
        backUrl = '/article';
      }
    }
    // Handle article listing page
    else if (currentUrl === '/article') {
      // If coming from article detail, go to home instead of back to detail
      if (previousUrl?.includes('/article/detail/')) {
        backUrl = '/';
      } else if (previousUrl && previousUrl !== currentUrl) {
        backUrl = previousUrl;
      } else {
        // Default to home for article listing
        backUrl = '/';
      }
    }
    // Handle settings pages - always go back to home
    else if (currentUrl.includes('/settings')) {
      backUrl = '/';
    }
    // Handle auth pages (login/signup) - prevent circular navigation
    else if (currentUrl === '/login' || currentUrl === '/signup') {
      // If coming from another auth page, go to home instead
      if (previousUrl === '/signup') {
        backUrl = '/';
      } else if (previousUrl && previousUrl !== currentUrl) {
        backUrl = previousUrl;
      } else {
        backUrl = '/';
      }
    }
    // Handle other pages - check if coming from a specific page
    else if (previousUrl && previousUrl !== currentUrl) {
      // If there's a valid previous URL, use it
      backUrl = previousUrl;
    }
    // Default case: go to home
    else {
      backUrl = '/';
    }

    this._sharedService.backButton.set({ show: true, url: backUrl, text: '', action: '' });
  }

  ngOnInit(){
    this.url = this._router.url;
    this.subscriptions = this._sharedService.user$.subscribe(user => {
      this.user = {... user};
      if (this.user.email) {
        this.isSignedIn = true;
        const selectedObject: langAndCaddy = {
          caddy: this.user.caddy,
          language: { name: this.user.language?.name || '', id: this.user.language?.id || '' }
        };
        this._sharedService.selectedCaddyAndLanguage.set(selectedObject);
      }
    });
    if (isPlatformBrowser(this._platformId)) {
      this._chatService.getGps();
      const isSignedIn = this._authService.isSignedIn();
      if (!isSignedIn) {
        this._sharedService.loading.set(false);
      }
    }
  }

  getUserSetting() {
    this._settingService.getSetting().pipe(
      switchMap(res => {
        const user = { ...res };
        const mediaRequests: Observable<any>[] = [];
        if (user.caddy?.image) {
          const caddyMedia$ = this._mediaService.getMedia([user.caddy.image]).pipe(
            catchError(error => {
              return of(null);
            }),
            map(mediaRes => ({ type: 'caddy', result: mediaRes }))
          );
          mediaRequests.push(caddyMedia$);
        }
  
        if (user.image) {
          if(user.image.includes("i.ibb.co")){
            user.imageUrl = user.image;
          }
          else{
            const userMedia$ = this._mediaService.getMedia([user.image]).pipe(
              catchError(error => {
                // console.error('Failed to load user image:', error);
                return of(null);
              }),
              map(mediaRes => ({ type: 'user', result: mediaRes }))
            );
            mediaRequests.push(userMedia$);
        }
        }
  
        return mediaRequests.length > 0
          ? forkJoin(mediaRequests).pipe(map(results => ({ user, results })))
          : of({ user, results: [] });
      }),
      take(1)
    ).subscribe({
      next: ({ user, results }) => {
        results.forEach(result => {
          if (!result) return;
          if (result.type === 'caddy' && result.result?.[0]?.url) {
            user.caddy = { ...user.caddy, imageUrl: result.result[0].url };
          }
          if (result.type === 'user' && result.result?.[0]?.url) {
            user.imageUrl = result.result[0].url;
          }
        });
        this._sharedService.setUser(user);
        this._translationService.setLanguage(user.language?.code || 'en');
        this._sharedService.loading.set(false);
        // this.router.navigateByUrl('/home');
        let socketEnabled = false;
        if (!socketEnabled) {
          // this._chatService.onListenWebsocket();
          socketEnabled = true;
        }
      },
      error: (error) => {
        this._toaster.error('Error fetching user settings:', error);
        this._sharedService.loading.set(false);
        // Consider implementing error handling logic here
      }
    });
  }

  setButtonAction(action: string){
    this._sharedService.backButtonAction.set({ type: 'action', action });
  }

  logOut() {
    this.showMenu = false;
    this._tokenService.logout();
  }

  setQuestion(question: string){
    this.showMenu = false;
    let questionModel = new AskQuestionModel();
    questionModel.textQuestion = question;
    questionModel.showProcessingText = true;
    this._sharedService.setQuestion(questionModel);
  }

  ngOnDestroy(){
    this.subscriptions.unsubscribe();
  }
}
